import { api } from '@/api/core';
import type { Citation } from '@/types/chat';
import { toPlainTextCitations } from '@/utils/htmlToPlainText';

/** BE run-level report and summary endpoint. Wire it up in `useAerialRunLevelReport` once the API exists. */
export const AERIAL_RUN_LEVEL_REPORT_URL = '/aerial/run/report';

export interface AerialRunLevelReportDocument {
  documentId: string;
  documentName: string;
}

export interface AerialRunLevelReportContent {
  title: string;
  content: string;
  citations: Record<string, Citation>;
  document: AerialRunLevelReportDocument;
}

export interface GetAerialRunLevelReportRequest {
  aerialId: string;
  runId: string;
}

export interface GetAerialRunLevelReportResponse {
  summary: string;
  hasReport: boolean;
  report?: AerialRunLevelReportContent;
}

const getAerialRunLevelReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAerialRunLevelReport: builder.query<GetAerialRunLevelReportResponse, GetAerialRunLevelReportRequest>({
      query: ({ aerialId, runId }) => ({
        url: AERIAL_RUN_LEVEL_REPORT_URL,
        method: 'GET',
        params: {
          aerial_id: aerialId,
          run_id: runId,
        },
      }),
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: GetAerialRunLevelReportResponse) => {
        if (!response.report) return response;
        // Same HTML-in-the-extract handling as `GET /chat`: the report reuses ChatMessage,
        // so its citations reach the same tooltip.
        return {
          ...response,
          report: { ...response.report, citations: toPlainTextCitations(response.report.citations) },
        };
      },
    }),
  }),
});

export const { useGetAerialRunLevelReportQuery } = getAerialRunLevelReportApi;
